import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Save } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { NewQuestion, Category } from './types';
import { ImageUpload } from './ImageUpload';

interface AddQuestionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onAddQuestion: (question: NewQuestion) => Promise<void>;
  categories: Category[];
}

const initialQuestionState: NewQuestion = {
  question_text: '',
  question_type: 'multiple_choice',
  options: ['', '', '', ''],
  correct_answer: [],
  explanation: '',
  created_by: '',
  question_images: [],
  explanation_images: [],
};

export const AddQuestionDialog = ({
  isOpen,
  onClose,
  onAddQuestion,
  categories,
}: AddQuestionDialogProps) => {
  const [newQuestion, setNewQuestion] = useState<NewQuestion>(initialQuestionState);
  const [uploading, setUploading] = useState(false);

  const handleSave = async () => {
    setUploading(true);
    // Ensure correct_answer is an array for non-multiple choice questions
    const questionToSave = {
      ...newQuestion,
      correct_answer: Array.isArray(newQuestion.correct_answer)
        ? newQuestion.correct_answer
        : [newQuestion.correct_answer],
    };
    await onAddQuestion(questionToSave);
    setUploading(false);
    setNewQuestion(initialQuestionState); // Reset form
    onClose();
  };

  const handleCorrectAnswerChange = (option: string) => {
    setNewQuestion(prev => {
      const currentAnswers = prev.correct_answer;
      if (currentAnswers.includes(option)) {
        return { ...prev, correct_answer: currentAnswers.filter(ans => ans !== option) };
      } else {
        return { ...prev, correct_answer: [...currentAnswers, option] };
      }
    });
  };

  const updateOption = (index: number, value: string) => {
    const newOptions = [...newQuestion.options];
    newOptions[index] = value;
    setNewQuestion(prev => ({ ...prev, options: newOptions }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add New Question</DialogTitle>
          <DialogDescription>
            Create a new question for your exam bank
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Question Type</Label>
            <Select 
              value={newQuestion.question_type} 
              onValueChange={(value) => setNewQuestion(prev => ({ ...prev, question_type: value }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="multiple_choice">Multiple Choice</SelectItem>
                <SelectItem value="true_false">True/False</SelectItem>
                <SelectItem value="essay">Essay</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Question Text</Label>
            <Textarea
              placeholder="Enter your question here..."
              value={newQuestion.question_text}
              onChange={(e) => setNewQuestion(prev => ({ ...prev, question_text: e.target.value }))}
              rows={3}
            />
          </div>

          {newQuestion.question_type === 'multiple_choice' && (
            <div className="space-y-2">
              <Label>Answer Options</Label>
              {newQuestion.options.map((option, index) => (
                <Input
                  key={index}
                  placeholder={`Option ${String.fromCharCode(65 + index)}`}
                  value={option}
                  onChange={(e) => updateOption(index, e.target.value)}
                />
              ))}
            </div>
          )}

          <div className="space-y-2">
            <Label>Correct Answer</Label>
            {newQuestion.question_type === 'multiple_choice' ? (
              <div className="space-y-2 rounded-md border p-2">
                <p className="text-sm text-muted-foreground">Select one or more correct answers.</p>
                {newQuestion.options.map((option, index) => (
                  option && (
                    <div key={index} className="flex items-center gap-2">
                      <Checkbox
                        id={`correct-answer-${index}`}
                        checked={newQuestion.correct_answer.includes(option)}
                        onCheckedChange={() => handleCorrectAnswerChange(option)}
                      />
                      <Label htmlFor={`correct-answer-${index}`} className="font-normal">
                        {option}
                      </Label>
                    </div>
                  )
                ))}
              </div>
            ) : newQuestion.question_type === 'true_false' ? (
              <Select 
                value={Array.isArray(newQuestion.correct_answer) ? newQuestion.correct_answer[0] : newQuestion.correct_answer} 
                onValueChange={(value) => setNewQuestion(prev => ({ ...prev, correct_answer: [value] }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select correct answer" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">True</SelectItem>
                  <SelectItem value="false">False</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <Textarea
                placeholder="Enter the correct answer or answer key..."
                value={Array.isArray(newQuestion.correct_answer) ? newQuestion.correct_answer.join(', ') : newQuestion.correct_answer}
                onChange={(e) => setNewQuestion(prev => ({ ...prev, correct_answer: e.target.value.split(',').map(s => s.trim()) }))}
              />
            )}
          </div>

          <div className="space-y-2">
            <Label>Explanation (Optional)</Label>
            <Textarea
              placeholder="Provide an explanation for the correct answer..."
              value={newQuestion.explanation}
              onChange={(e) => setNewQuestion(prev => ({ ...prev, explanation: e.target.value }))}
              rows={2}
            />
          </div>

          <ImageUpload
            label="Question Images"
            images={newQuestion.question_images || []}
            onImagesChange={(images) => setNewQuestion(prev => ({ ...prev, question_images: images }))}
            maxImages={3}
          />

          <ImageUpload
            label="Explanation Images"
            images={newQuestion.explanation_images || []}
            onImagesChange={(images) => setNewQuestion(prev => ({ ...prev, explanation_images: images }))}
            maxImages={3}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={uploading}>
            <Save className="h-4 w-4 mr-2" />
            {uploading ? 'Saving...' : 'Save Question'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};